/**
 * Requests
 */

import axios from 'axios'
import { apiUrl } from '@/config'

export class BaseAPIClient {
  constructor () {
    this.client = axios.create({
      baseURL: apiUrl,
      headers: { 'Content-Type': 'application/json' },
      timeout: 15000
    })

    this.setupInterceptors()
  }

  setupInterceptors () {
    this.client.interceptors.request.use(config => {
      config.store = this.getStore()
      const { token } = config.store.state

      if (token) {
        config.headers.common.Authorization = `JWT ${token}`
      }

      console.debug('request sent to', config.url, 'with token', token?.split('.')[2])
      return config
    }, error => {
      console.debug(error.message)
      const { store } = error.config
      store.commit('notify/requestError')
    })

    this.client.interceptors.response.use(successHandler, errorHandler)
  }

  getStore () {
    throw new Error('This method must be implemented in a child class.')
  }
}

const successHandler = response => {
  console.debug('response received', response)
  return response.data
}

const errorHandler = error => {
  const { status } = error.response
  console.debug('intercepting the error, status', status)

  switch (status) {
    case 401:
      return handleAuthFailure(error).then(data => data)
    case 404:
      handleNotFound(error)
      break
    case 500:
      handleNetworkError(error)
  }

  console.debug('reject the error')
  return Promise.reject(error)
}

const handleNotFound = error => {
  console.debug('handling not found error')
  const { store } = error.config
  store.commit('notify/notFoundError')
}

const handleNetworkError = error => {
  console.debug('handling network error')
  const { store } = error.config
  store.commit('notify/serverError')
}

const handleAuthFailure = error => {
  console.debug('handling auth failure', error.config.url)
  const { config } = error
  const { store } = config

  const failedRefresh = () => config.url === '/auth/token/refresh/'

  if (!failedRefresh() && (store.getters.loggedIn || store.state.loggingIn)) {
    return tryTokenRefresh(store)
      .then(() => {
        if (store.state.token) {
          console.debug('token refreshed, retry with token', store.state.token?.split('.')[2])
          config.headers.Authorization = `JWT ${store.state.token}`
          return store.state.client.client.request(config)
        }
        console.debug('reject refresh failure')
      })
  }
  console.debug('reject auth failure')
  return Promise.reject(error)
}

const tryTokenRefresh = (store) => {
  console.debug('try token refresh')
  if (store.state.refreshInProgress) {
    console.debug('already refreshing')
    return store.state.refreshRequest
  }

  store.commit('setRefreshStatus', true)
  const refreshRequest = store.dispatch('refreshToken')
    .then(data => {
      store.commit('setToken', data.access)
      localStorage.setItem('access', JSON.stringify(data.access))
      store.commit('setRefreshStatus', false)
      store.commit('setRefreshRequest', null)
      console.debug('token received and set')
      return Promise.resolve(true)
    })
    .catch(() => {
      store.commit('notify/refreshError')
      console.debug('token refresh failure at utils.http.js:121')
      store.dispatch('logout')
    })

  console.debug('set refresh request')
  store.commit('setRefreshRequest', refreshRequest)
  return refreshRequest
}
